import { useWindowSize } from "@/utils/hooksUtils";
import { cssVariables, getActiveEnvProfile, getDefaultLogo, isDefined } from "@/utils/utils";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { ConfigProvider, Grid, Layout, Skeleton, Spin } from "antd";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router";
import { useEnumerations } from "../../../common/api/queries";
import { ViewBreakpoints } from "../../../common/constants";
import { useBrandingSync } from "../../../modules/branding/hooks";
import { useBranding } from "../../../modules/branding/store";
import { DASHBOARD_ROUTE_PATHS, DASHBOARD_ROUTE_PREFIX } from "../../../modules/dashboard/paths";
import styles from "../../containers/App.module.scss";
import { useMenuCollapse } from "../../contexts/MenuCollapseContext";
import { BreadcrumbsBox } from "../BreadcrumbsBox";
import { Footer } from "../footer/Footer";
import { Header } from "../header/Header";
import SideMenu from "../sidemenu/SideMenu";

const { useBreakpoint } = Grid;

export const AppLayout = () => {
  const { xl } = useBreakpoint();

  const { pathname } = useLocation();

  const [isNotFoundPageRendered, setIsNotFoundPageRendered] = useState(false);

  const { isMenuCollapsed, setIsMenuCollapsed } = useMenuCollapse();

  // A query blocks the page only until it has data - a background refetch never does.
  const fetchingCount = useIsFetching({
    predicate: (query) => query.state.data === undefined && query.meta?.globalSpinner !== false,
  });
  const mutatingCount = useIsMutating({ predicate: (mutation) => mutation.meta?.globalSpinner !== false });
  const showSpinner = fetchingCount + mutatingCount > 0;
  const branding = useBranding();

  // The Outlet waits for the enumerations, also when their first load fails - pages would otherwise render with empty
  // selects and missing names. A failed refetch of cached data keeps rendering the stale enumerations.
  const { isPending, isLoadingError } = useEnumerations();
  useBrandingSync();

  const windowSize = useWindowSize();

  useEffect(() => {
    if (isDefined(xl)) {
      setIsMenuCollapsed(!xl);
    }
  }, [xl]);

  if (windowSize.outerWidth < ViewBreakpoints.TABLET) {
    document.querySelector("meta[name=viewport]")?.setAttribute("content", "width=991");
  } else {
    document
      .querySelector("meta[name=viewport]")
      ?.setAttribute("content", "width=device-width, initial-scale=1, shrink-to-fit=no");
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: branding?.primaryColor ?? cssVariables.colorPrimary,
          colorText: cssVariables.colorText,
        },
      }}
    >
      <Layout hasSider>
        <Layout.Sider
          collapsible
          collapsed={isMenuCollapsed}
          onCollapse={setIsMenuCollapsed}
          collapsedWidth={cssVariables.sideMenuResponsiveWidth}
          width={cssVariables.sideMenuWidth}
          style={{ position: "fixed", overflow: "auto", height: "100svh", top: 0, bottom: 0 }}
        >
          <div className={styles.logo}>
            <Link to={DASHBOARD_ROUTE_PATHS.homepage.to} className={styles.logoLink}>
              <img
                className={classNames(styles.logoImage, { [styles.logoImageCollapsed as string]: isMenuCollapsed })}
                src={
                  branding?.logoResourcePath
                    ? `/images/${branding.logoResourcePath}`
                    : isMenuCollapsed
                      ? getDefaultLogo("square")
                      : getDefaultLogo("light")
                }
                alt="Logo"
              />
            </Link>
          </div>

          <SideMenu isMenuCollapsed={isMenuCollapsed} />
        </Layout.Sider>

        <Layout
          style={{
            marginInlineStart: isMenuCollapsed ? cssVariables.sideMenuResponsiveWidth : cssVariables.sideMenuWidth,
          }}
          className={`${styles.rootLayout} root-layout--signed ${getActiveEnvProfile()}`}
        >
          <Header isMenuCollapsed={isMenuCollapsed} />

          <Layout.Content className={styles.layoutContent}>
            <Spin spinning={showSpinner} size="large">
              <div className={styles.mainWrapper}>
                {isPending || isLoadingError ? (
                  <>
                    <Skeleton active paragraph={{ rows: 6 }} />
                    <Skeleton active paragraph={{ rows: 3 }} />
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </>
                ) : (
                  <>
                    {pathname !== DASHBOARD_ROUTE_PREFIX && !isNotFoundPageRendered ? <BreadcrumbsBox /> : undefined}
                    <Outlet context={setIsNotFoundPageRendered} />
                  </>
                )}
              </div>
            </Spin>
          </Layout.Content>

          <Footer />
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};
